import _styled, { default as _styled2 } from "styled-components";
import { action, runInAction } from "mobx";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { addMarker, removeMarker } from "../../Models/LocationMarkerUtils";
import Box from "../../Styled/Box";
import { RawButton } from "../../Styled/Button";
import Icon, { StyledIcon } from "../../Styled/Icon";
import Spacing from "../../Styled/Spacing";
import Text from "../../Styled/Text";
import { useViewState } from "../Context";
import LocationSearchResults from "./LocationSearchResults";
import SearchBox from "./SearchBox";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function SearchInDataCatalog(_ref) {
  let {
    handleClick
  } = _ref;
  const viewState = useViewState();
  const locationSearchText = viewState.searchState.locationSearchText;
  const {
    t
  } = useTranslation();
  return /*#__PURE__*/_jsx(RawButton, {
    fullWidth: true,
    onClick: () => {
      const {
        searchState
      } = viewState;
      // Set text here as a separate action so that it doesn't get batched up and the catalog
      // search text has a chance to set isWaitingToStartCatalogSearch
      searchState.setCatalogSearchText(searchState.locationSearchText);
      viewState.searchInCatalog(searchState.locationSearchText);
      if (handleClick) {
        handleClick();
      }
    },
    children: /*#__PURE__*/_jsxs(Box, {
      paddedRatio: 2,
      rounded: true,
      charcoalGreyBg: true,
      children: [/*#__PURE__*/_jsx(StyledIcon, {
        styledWidth: "14px",
        glyph: Icon.GLYPHS["dataCatalog"]
      }), /*#__PURE__*/_jsx(Spacing, {
        right: 2
      }), /*#__PURE__*/_jsx(Text, {
        textAlignLeft: true,
        textLight: true,
        large: true,
        fullWidth: true,
        children: t("search.searchInDataCatalog", {
          locationSearchText: locationSearchText
        })
      }), /*#__PURE__*/_jsx(StyledIcon, {
        glyph: Icon.GLYPHS.right2,
        styledWidth: "14px",
        light: true
      })]
    })
  });
}
SearchInDataCatalog.propTypes = {
  handleClick: PropTypes.func.isRequired,
  viewState: PropTypes.object.isRequired
};
const PresentationBox = styled(Box).attrs({
  fullWidth: true
}).withConfig({
  displayName: "SearchBoxAndResults__PresentationBox",
  componentId: "sc-11p148l-0"
})(["", ""], props => props.highlightBottom && "\n      // styled-components doesn't seem to prefix linear-gradient.. soo\n      background-image: linear-gradient(bottom, ".concat(props.theme.greyLightest, " 50%, transparent 50%);\n      background-image: -o-linear-gradient(bottom, ").concat(props.theme.greyLightest, " 50%, transparent 50%);\n      background-image: -moz-linear-gradient(bottom, ").concat(props.theme.greyLightest, " 50%, transparent 50%);\n      background-image: -webkit-linear-gradient(bottom, ").concat(props.theme.greyLightest, " 50%, transparent 50%);\n      background-image: -ms-linear-gradient(bottom, ").concat(props.theme.greyLightest, " 50%, transparent 50%);\n    "));
export const LOCATION_SEARCH_INPUT_NAME = "LocationSearchInput";
export const SearchBoxAndResults = observer(_ref2 => {
  let {
    placeholder
  } = _ref2;
  const locationSearchRef = useRef(null);
  const viewState = useViewState();
  const theme = useTheme();
  useEffect(() => {
    viewState.updateAppRef(LOCATION_SEARCH_INPUT_NAME, locationSearchRef);
  }, [viewState]);
  const toggleShowLocationSearchResults = action(bool => {
    viewState.searchState.showLocationSearchResults = bool;
  });
  const changeSearchText = newText => {
    runInAction(() => {
      viewState.searchState.locationSearchText = newText;
    });
    if (newText.length === 0) {
      removeMarker(viewState.terria);
      runInAction(() => {
        toggleShowLocationSearchResults(false);
      });
    }
    if (newText.length > 0 && !viewState.searchState.showLocationSearchResults) {
      runInAction(() => {
        toggleShowLocationSearchResults(true);
      });
    }
  };
  const search = () => {
    viewState.searchState.searchLocations();
  };
  const startLocationSearch = () => {
    toggleShowLocationSearchResults(true);
  };
  const searchState = viewState.searchState;
  const locationSearchText = searchState.locationSearchText;
  const shouldShowResults = searchState.locationSearchText.length > 0 && searchState.showLocationSearchResults;
  return /*#__PURE__*/_jsx(Text, {
    textDarker: true,
    children: /*#__PURE__*/_jsxs(Box, {
      fullWidth: true,
      children: [/*#__PURE__*/_jsx(PresentationBox, {
        highlightBottom: shouldShowResults,
        children: /*#__PURE__*/_jsx(SearchBox, {
          ref: locationSearchRef,
          onSearchTextChanged: changeSearchText,
          onDoSearch: search,
          onFocus: startLocationSearch,
          searchText: searchState.locationSearchText,
          placeholder: placeholder,
          supportsAutocomplete: searchState.supportsAutocomplete
        })
      }), shouldShowResults && /*#__PURE__*/_jsxs(_StyledBox, {
        position: "absolute",
        fullWidth: true,
        column: true,
        $_css: theme.greyLightest,
        $_css2: theme.radiusLarge,
        $_css3: theme.radiusLarge,
        children: [viewState.terria.searchBarModel.showSearchInCatalog && searchState.catalogSearchProvider && /*#__PURE__*/_jsx(Box, {
          column: true,
          paddedRatio: 2,
          children: /*#__PURE__*/_jsx(SearchInDataCatalog, {
            viewState: viewState,
            handleClick: () => {
              toggleShowLocationSearchResults(false);
            }
          })
        }), /*#__PURE__*/_jsx(_StyledBox2, {
          column: true,
          children: !searchState.isWaitingToStartLocationSearch && searchState.locationSearchResults.map(search => /*#__PURE__*/_jsx(LocationSearchResults, {
            terria: viewState.terria,
            viewState: viewState,
            search: search,
            locationSearchText: locationSearchText,
            onLocationClick: result => {
              var _result$clickAction;
              if (!result.location) return;
              addMarker(viewState.terria, {
                name: result.name,
                location: result.location
              });
              (_result$clickAction = result.clickAction) === null || _result$clickAction === void 0 || _result$clickAction.call(result);
              runInAction(() => {
                searchState.showLocationSearchResults = false;
              });
            },
            isWaitingForSearchToStart: searchState.isWaitingToStartLocationSearch
          }, search.searchProvider.uniqueId))
        })]
      })]
    })
  });
});
SearchBoxAndResults.displayName = "SearchBoxAndResults";
export default SearchBoxAndResults;
var _StyledBox = styled(Box).withConfig({
  displayName: "SearchBoxAndResults___StyledBox",
  componentId: "sc-11p148l-1"
})(["top:100%;background-color:", ";max-height:calc(100vh - 200px);border-radius:0 0 ", " ", ";overflow:hidden;"], p => p.$_css, p => p.$_css2, p => p.$_css3);
var _StyledBox2 = styled(Box).withConfig({
  displayName: "SearchBoxAndResults___StyledBox2",
  componentId: "sc-11p148l-2"
})(["overflow-y:auto;"]);